/**
 * Analytics query validation and time calculation utilities
 */
const { DateTime } = require('luxon');

/**
 * Checks if queryMode is valid.
 * @param {string} queryMode - The query mode ('hour', 'day', 'week', 'month', 'year')
 * @returns {boolean}
 */
export function isQueryModeValid(queryMode) {
  return ['hour', 'day', 'week', 'month', 'year'].includes(queryMode);
}

/**
 * Checks if value is a positive integer
 * @param {number} value
 * @returns {boolean}
 */
export function isPositiveInteger(value) {
  return Number.isInteger(value) && value > 0;
}

/**
 * Checks if timeStride is valid.
 * @param {number} timeStride
 * @returns {boolean}
 */
export function isTimeStrideValid(timeStride) {
  return isPositiveInteger(timeStride);
}

/**
 * Checks if timeAmount is valid.
 * @param {number} timeAmount
 * @returns {boolean}
 */
export function isTimeAmountValid(timeAmount) {
  return isPositiveInteger(timeAmount);
}

/**
 * Checks if entire query is valid.
 * @param {string} queryMode
 * @param {number} timeAmount
 * @param {number} timeStride
 * @returns {boolean}
 */
export function isQueryValid(queryMode, timeAmount, timeStride) {
  return (
    isQueryModeValid(queryMode) && isTimeAmountValid(timeAmount) && isTimeStrideValid(timeStride)
  );
}

/**
 * Converts queryMode to a valid DateTime param.
 * @param {string} queryMode
 * @returns {string}
 */
export function convertQueryModeToDateTimeParam(queryMode) {
  return queryMode === 'month' ? queryMode : queryMode + 's';
}

/**
 * Gets start and end times based on queryMode by specified timeUnits
 * @param {string} queryMode
 * @param {number} timeUnits
 * @returns {[DateTime, DateTime]} [startTime, endTime]
 */
export function getStartAndEndTimes(queryMode, timeUnits) {
  const timeNow = DateTime.now();
  // For day/hour modes, use current moment for a rolling window (matches View Reports).
  // For week/month, snap to period boundary for clean chart alignment.
  const useRolling = queryMode === 'hour' || queryMode === 'day';
  const endTime = useRolling ? timeNow : timeNow.endOf(queryMode);
  const startTime = endTime.minus({
    [convertQueryModeToDateTimeParam(queryMode)]: timeUnits,
  });
  return [startTime, endTime];
}

/**
 * Calculate bucket start times for a time period based on queryMode.
 * @param {string} queryMode
 * @param {DateTime} endTime
 * @param {number} timeStride
 * @param {number} timeAmount
 * @returns {DateTime[]}
 */
export function getBucketStartTimes(queryMode, endTime, timeStride, timeAmount) {
  const bucketStartTimes = [];
  for (let i = timeAmount; i >= 1; i--) {
    let bucketStartTime = endTime.minus({
      [convertQueryModeToDateTimeParam(queryMode)]: i * timeStride,
    });
    if (queryMode !== 'hour') {
      bucketStartTime = bucketStartTime.endOf(queryMode);
    }
    bucketStartTimes.push(bucketStartTime);
  }
  return bucketStartTimes;
}

/**
 * Get x-axis date labels for a time period based on queryMode.
 * @param {string} queryMode
 * @param {DateTime[]} bucketStartTimes
 * @returns {string[]}
 */
export function getDateLabels(queryMode, bucketStartTimes) {
  const format = {
    hour: 'HH:mm',
    day: 'MM/dd',
    week: 'MM/dd',
    month: 'MM/yy',
    year: 'yyyy',
  };

  const plus_hours = {
    hour: 0,
    day: 12,
    week: 1,
    month: 1,
    year: 1,
  };

  const dateLabels = [];
  bucketStartTimes.forEach((st) => {
    let label = st;
    if (plus_hours[queryMode] > 0) {
      label = label.plus({ hours: plus_hours[queryMode] });
    }
    dateLabels.push(label.startOf(queryMode).toFormat(format[queryMode]));
  });

  return dateLabels;
}

/**
 * Build buckets for a time series given a number of buckets and categories
 * @param {number} buckets
 * @param {string[]} categories
 * @returns {Object}
 */
export function getTimeSeries(buckets, categories) {
  const timeSeries = {};
  timeSeries['ALL'] = Array.from({ length: buckets }, () => 0);
  for (let category of categories) {
    timeSeries[category] = Array.from({ length: buckets }, () => 0);
  }
  return timeSeries;
}

/**
 * Filter reports from startTime to endTime.
 * @param {Array} allReports
 * @param {DateTime} startTime
 * @param {DateTime} endTime
 * @returns {Array}
 */
export function filterReports(allReports, startTime, endTime) {
  let filteredReports = [];
  for (let report of allReports) {
    if (report.timestamp >= startTime.toMillis() && report.timestamp <= endTime.toMillis()) {
      filteredReports.push(report);
    }
  }
  return filteredReports;
}
